import React, { useEffect, useState } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { getSurveyResponses } from 'services/reports';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SurveyResponseDetailsModal from './SurveyResponseDetailsModal';
import PropTypes from 'prop-types';

const ResponsesTable = ({ surveyId }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResponseId, setSelectedResponseId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setResponses([]); // Limpia datos previos

    getSurveyResponses(surveyId)
      .then(response => {
        if (isMounted) {
          setResponses(response.result ? response.data : []);
        }
      })
      .catch(() => {
        if (isMounted) setResponses([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [surveyId]);

  const handleViewResponse = response => {
    setSelectedResponseId(response.id); // Establece el ID de la respuesta seleccionada
    setShowModal(true); // Abre el modal
  };

  const handleCloseModal = () => {
    setShowModal(false); // Cierra el modal
    setSelectedResponseId(null); // Limpia el ID seleccionado
  };

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      meta: {
        headerProps: { className: 'text-900' },
        cellProps: { className: 'py-2' }
      }
    },
    {
      accessorKey: 'user_ip',
      header: 'IP del Usuario',
      meta: {
        headerProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'created_at',
      header: 'Fecha de Creación',
      meta: {
        headerProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'completed_at',
      header: 'Fecha de Finalización',
      meta: {
        headerProps: { className: 'text-900' }
      }
    },
    {
      id: 'actions', // Columna para acciones
      header: 'Acciones',
      cell: ({ row }) => (
        <div className="text-center">
          <FontAwesomeIcon
            icon="eye"
            className="text-primary cursor-pointer"
            title="Ver Respuesta"
            onClick={() => handleViewResponse(row.original)}
          />
        </div>
      ),
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'py-2 text-center' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: responses,
    columns,
    sortable: true, // Habilita el ordenamiento
    pagination: true, // Habilita la paginación
    perPage: 5 // Número de filas por página
  });

  return (
    <>
      <Row className="g-3 mb-2">
        <Col xs={12}>
          <AdvanceTableProvider {...table}>
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0 text-center text-md-start">
                  Respuestas de la Encuesta
                </h5>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                  </div>
                ) : responses.length === 0 ? (
                  <div className="text-center py-3 text-muted">
                    No hay respuestas disponibles para esta encuesta.
                  </div>
                ) : (
                  <AdvanceTable
                    headerClassName="bg-200 text-nowrap align-middle"
                    rowClassName="align-middle white-space-nowrap"
                    tableProps={{
                      size: 'sm',
                      striped: true,
                      bordered: true,
                      className: 'fs-10 mb-0 overflow-hidden',
                      style: {
                        borderWidth: 1,
                        borderColor: '#343a40',
                        borderStyle: 'solid'
                      }
                    }}
                  />
                )}
              </Card.Body>
              <Card.Footer>
                <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
              </Card.Footer>
            </Card>
          </AdvanceTableProvider>

          {/* Modal para ver los detalles de la respuesta */}
          <SurveyResponseDetailsModal
            surveyResponseId={selectedResponseId}
            show={showModal}
            handleClose={handleCloseModal}
          />
        </Col>
      </Row>
    </>
  );
};

ResponsesTable.propTypes = {
  surveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ResponsesTable;
